import { CommonModule } from '@angular/common';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormControl, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { DropdownModule } from 'primeng/dropdown';
import { InputTextModule } from 'primeng/inputtext';
import { TableModule, TablePageEvent } from 'primeng/table';
import { TagModule } from 'primeng/tag';
import { TooltipModule } from 'primeng/tooltip';
import { MessageType, UtilsService } from 'src/app/common';
import { UserDeletionOperation } from '../../config';
import { AdminPanelService } from '../../services/admin-panel.service';

@Component({
  selector: 'app-deletion-operations',
  standalone: true,
  imports: [
    ButtonModule,
    CommonModule,
    DialogModule,
    DropdownModule,
    FormsModule,
    InputTextModule,
    ReactiveFormsModule,
    TableModule,
    TagModule,
    TooltipModule
  ],
  templateUrl: './deletion-operations.component.html',
  styleUrl: './deletion-operations.component.scss'
})
export class DeletionOperationsComponent implements OnInit, OnDestroy {
  public readonly retryReason = new FormControl<string>('', {
    nonNullable: true,
    validators: [Validators.required, Validators.minLength(3), Validators.maxLength(500)]
  });
  public readonly statusOptions = [
    { label: 'All statuses', value: '' },
    { label: 'Action required', value: 'action-required' },
    { label: 'Retry pending', value: 'retry-pending' },
    { label: 'Exhausted', value: 'exhausted' },
    { label: 'In progress', value: 'in-progress' },
    { label: 'Completed', value: 'completed' }
  ];
  public operations: UserDeletionOperation[] = [];
  public totalRecords = 0;
  public page = 1;
  public pageSize = 25;
  public search = '';
  public status = '';
  public loading = false;
  public retrying = false;
  public showRetryDialog = false;
  public selectedOperation: UserDeletionOperation | null = null;
  private searchTimer: number | null = null;

  public constructor(
    private readonly adminPanelService: AdminPanelService,
    private readonly utils: UtilsService
  ) { }

  public ngOnInit(): void {
    void this.loadOperations();
  }

  public ngOnDestroy(): void {
    if (this.searchTimer !== null) window.clearTimeout(this.searchTimer);
  }

  public async loadOperations(): Promise<void> {
    this.loading = true;
    const response = await this.adminPanelService.getUserDeletionOperations({
      page: this.page,
      pageSize: this.pageSize,
      status: this.status,
      q: this.search,
      handleError: true
    });
    if (response.success && response.data) {
      this.operations = response.data.items;
      this.totalRecords = response.data.totalCount;
    }
    this.loading = false;
  }

  public onPage(event: TablePageEvent): void {
    this.pageSize = event.rows;
    this.page = Math.floor(event.first / event.rows) + 1;
    void this.loadOperations();
  }

  public onSearch(value: string): void {
    this.search = value;
    if (this.searchTimer !== null) window.clearTimeout(this.searchTimer);
    this.searchTimer = window.setTimeout(() => {
      this.page = 1;
      void this.loadOperations();
    }, 300);
  }

  public onStatusChange(): void {
    this.page = 1;
    void this.loadOperations();
  }

  public canRetry(operation: UserDeletionOperation): boolean {
    return operation.status === 'action-required'
      || operation.status === 'retry-pending'
      || operation.status === 'exhausted';
  }

  public openRetry(operation: UserDeletionOperation): void {
    if (!this.canRetry(operation)) return;
    this.selectedOperation = operation;
    this.retryReason.reset();
    this.showRetryDialog = true;
  }

  public closeRetry(): void {
    if (this.retrying) return;
    this.showRetryDialog = false;
    this.selectedOperation = null;
    this.retryReason.reset();
  }

  public async submitRetry(): Promise<void> {
    this.retryReason.markAsTouched();
    if (!this.selectedOperation || this.retryReason.invalid || this.retrying) return;
    this.retrying = true;
    const response = await this.adminPanelService.retryUserDeletionOperation(
      this.selectedOperation.operationId,
      this.retryReason.value,
      { handleError: true }
    );
    this.retrying = false;
    if (!response.success) return;
    this.utils.successMessage(MessageType.TOAST, 'Retry Submitted', 'The deletion operation was retried.');
    this.closeRetry();
    await this.loadOperations();
  }

  public statusSeverity(status: string): 'success' | 'warning' | 'danger' | 'info' {
    if (status === 'completed' || status === 'deleted' || status === 'already-absent') return 'success';
    if (status === 'exhausted' || status === 'failed') return 'danger';
    if (status === 'action-required' || status === 'retry-pending') return 'warning';
    return 'info';
  }
}
